<template>
  <Modal
    title="Select Site Address"
    modal-classes="modal-xl"
    :disable-save="isSavingAddress"
    :save-button-text="isSavingAddress ? 'Saving...' : 'Save'"
    @close="() => emit('close')"
    @save="selectAddress"
  >
    <form @submit.prevent="selectAddress">
      <div v-if="existingAddresses.length > 0" class="mb-3">
        <label class="form-label">Select Existing Customer Site Address</label>
        <select
          v-model="selectedAddressId"
          class="form-select"
          @change="emitSelectedAddress"
        >
          <option :value="null">Create a new site address</option>
          <option
            v-for="address in existingAddresses"
            :key="address.id"
            :value="address.id"
          >
            {{ address.line_1 }}, {{ address.city }},
            {{ address.postcode }}
          </option>
        </select>
      </div>
      <div
        v-if="existingAddresses.length > 0"
        style="font-size: 20px"
        class="mb-3 text-center text-muted"
      >
        Or Enter Manually
      </div>
      <address-autocomplete
        class="mb-4"
        @addressSelected="handleAddressSelected"
      ></address-autocomplete>
      <div class="row g-4">
        <div class="col-lg-6">
          <div class="mb-3">
            <label><strong>Address Line 1</strong></label>
            <br />
            <input
              v-model="siteAddress.line_1"
              type="text"
              :class="{ 'is-invalid': errors.line_1 }"
              class="form-control"
              required
              @input="switchToManualAddress"
            />
            <div v-if="errors.line_1" class="invalid-feedback">
              {{ errors.line_1 }}
            </div>
          </div>
          <div class="mb-3">
            <label><strong>Address Line 2</strong></label>
            <br />
            <input
              v-model="siteAddress.line_2"
              type="text"
              :class="{ 'is-invalid': errors.line_2 }"
              class="form-control"
              @input="switchToManualAddress"
            />
            <div v-if="errors.line_2" class="invalid-feedback">
              {{ errors.line_2 }}
            </div>
          </div>
          <div class="mb-3">
            <label><strong>City</strong></label>
            <br />
            <input
              v-model="siteAddress.city"
              type="text"
              :class="{ 'is-invalid': errors.city }"
              class="form-control"
              required
              @input="switchToManualAddress"
            />
            <div v-if="errors.city" class="invalid-feedback">
              {{ errors.city }}
            </div>
          </div>
          <div>
            <label><strong>Postcode</strong></label>
            <br />
            <input
              v-model="siteAddress.postcode"
              type="text"
              :class="{ 'is-invalid': errors.postcode }"
              class="form-control"
              required
              @input="switchToManualAddress"
            />
            <div v-if="errors.postcode" class="invalid-feedback">
              {{ errors.postcode }}
            </div>
          </div>
          <div class="mt-3">
            <label><strong>what3words</strong></label>
            <br />
            <input
              v-model="siteAddress.what3words"
              type="text"
              :class="{ 'is-invalid': errors.what3words }"
              class="form-control"
              placeholder="filled.count.soap"
              @input="handleWhat3WordsInput"
            />
            <div v-if="errors.what3words" class="invalid-feedback">
              {{ errors.what3words }}
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <AddressLocationPicker
            :model-value="siteAddress.coords"
            @update:modelValue="updateCoordinates"
          />
        </div>
      </div>
    </form>
  </Modal>
</template>

<script setup>
import { ref } from "vue";
import AddressLocationPicker from "@/components/AddressLocationPicker.vue";
import AddressAutocomplete from "@/components/addressAutocomplete.vue";
import { useFetch } from "@/components/useFetch.js";
import Modal from "@/components/modal.vue";

const props = defineProps({
  customer: {
    type: Object,
    default: () => ({
      id: null,
      name: null,
    }),
  },
  selectedAddress: {
    type: Object,
    default: () => ({
      id: null,
      line_1: null,
      line_2: null,
      city: null,
      postcode: null,
      what3words: null,
      coords: {
        longitude: null,
        latitude: null,
      },
    }),
  },
});

function createCoordinates(source = {}) {
  return {
    longitude: source?.coords?.longitude ?? source?.longitude ?? null,
    latitude: source?.coords?.latitude ?? source?.latitude ?? null,
  };
}

function createSiteAddress(address = {}) {
  return {
    id: address.id ?? null,
    customer_id: props.customer.id ?? address.customer_id ?? null,
    line_1: address.line_1 ?? null,
    line_2: address.line_2 ?? null,
    line_3: address.line_3 ?? null,
    city: address.city ?? address.town_or_city ?? null,
    county: address.county ?? null,
    postcode: address.postcode ?? null,
    what3words: address.what3words ?? null,
    coords: createCoordinates(address),
  };
}

const siteAddress = ref(createSiteAddress(props.selectedAddress));
const existingAddresses = ref([]);
const selectedAddressId = ref(props.selectedAddress.id ?? null);
const errors = ref({});
const isSavingAddress = ref(false);

function switchToManualAddress() {
  selectedAddressId.value = null;
}

function updateCoordinates(coords) {
  switchToManualAddress();
  siteAddress.value.coords = createCoordinates(coords);
}

function handleWhat3WordsInput() {
  switchToManualAddress();
  if (!siteAddress.value.what3words) {
    siteAddress.value.what3words = null;
  }
}

function handleAddressSelected(address) {
  switchToManualAddress();
  siteAddress.value = createSiteAddress(address);
}

function emitSelectedAddress() {
  const selectedAddress = existingAddresses.value.find(
    (address) => address.id === selectedAddressId.value
  );

  if (!selectedAddress) {
    return;
  }

  siteAddress.value = createSiteAddress(selectedAddress);
}

async function fetchExistingAddresses() {
  const response = await useFetch(
    route("api.customers.site-addresses", { customer: props.customer.id })
  );
  existingAddresses.value = await response.json();
}

if (props.customer.id) {
  fetchExistingAddresses();
}

async function selectAddress() {
  if (isSavingAddress.value) {
    return;
  }

  errors.value = {};

  const selectedAddress = existingAddresses.value.find(
    (address) => address.id === selectedAddressId.value
  );

  if (!selectedAddress) {
    isSavingAddress.value = true;

    try {
      const response = await useFetch(
        route("api.customers.site-addresses.store", {
          customer: props.customer.id,
        }),
        {
          method: "POST",
          body: siteAddress.value,
        }
      );
      const result = await response.json();

      if (response.ok) {
        existingAddresses.value = existingAddresses.value.filter(
          (address) => address.id !== result.id
        );
        existingAddresses.value.unshift(result);
        siteAddress.value = createSiteAddress(result);
        selectedAddressId.value = result.id;
        emit("address-selected", result);
      } else {
        if (result.errors) {
          for (const key in result.errors) {
            if (Array.isArray(result.errors[key])) {
              errors.value[key] = result.errors[key].join(" ");
            } else {
              errors.value[key] = result.errors[key];
            }
          }
        } else {
          alert("Failed to save address");
        }
      }
    } finally {
      isSavingAddress.value = false;
    }
  } else {
    emit("address-selected", selectedAddress);
  }
}

const emit = defineEmits(["close", "address-selected"]);
</script>
